//
// DISCLAIMER
//
// Copyright 2022 ArangoDB GmbH, Cologne, Germany
//

import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Segment, Loader } from "semantic-ui-react";
import moment from "moment";
import { Timestamp } from "../../api/googleTypes";
import { ICommentContentViewArgs } from "./CommentTypes";
import UserCache, { UserWithDefinedID } from "../../util/UserCache";
import { DateTimePopupWithUTCAndLocalTime } from "../../util/dateAndTimeUtils/DateTime";

const StyledSegment = styled(Segment)`
  padding: 10px 25px !important;
  -moz-transition: all 2s ease-out;
  -o-transition: all 2s ease-out;
  transition: all 2s ease-out;
`;

const CommentHeading = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 6px;
  color: #6b7280;
  font-size: 12px;
`;

const CommentBody = styled.div`
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const StyledLoader = styled(Loader)`
  &::before {
    border-color: #e9e9e9;
  }
  &::after {
    border-color: #577138 transparent transparent !important;
  }
`;

export const isRecent = (createdAt: Timestamp | undefined) => {
  if (!createdAt) {
    return false;
  }
  const NEW_COMMENTS_DATE_LIMIT_IN_DAYS = 7;
  return moment().diff(moment(createdAt), "days") < NEW_COMMENTS_DATE_LIMIT_IN_DAYS;
};

const CommentItem = ({ comment }: ICommentContentViewArgs) => {
  const { comment: text = "", created_at: createdAt } = comment;
  const isCreatedNow = moment().diff(moment(createdAt), "seconds") < 20;
  const [user, setUser] = useState<UserWithDefinedID>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const cache = new UserCache();
      setLoading(true);
      try {
        const loaded = await cache.getUser(comment.created_by_id || "");
        if (!cancelled) {
          setUser(loaded);
        }
      } catch {
        if (!cancelled) {
          setUser(undefined);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [comment.created_by_id]);

  return (
    <StyledSegment attached basic={isCreatedNow}>
      <CommentHeading>
        <span>
          {loading ? (
            <>
              <StyledLoader active inline size="mini" /> Fetching user
            </>
          ) : (
            <>
              Created by <b>{user?.name || "Unknown"}</b>
            </>
          )}
        </span>
        <DateTimePopupWithUTCAndLocalTime dateTime={comment.created_at} label="Created at" />
      </CommentHeading>
      <CommentBody>{text}</CommentBody>
    </StyledSegment>
  );
};

export default CommentItem;
